/**
 * MufynButton - Composant Button unifié pour Mufyn
 * 
 * Ce composant centralise tous les styles de boutons de l'application.
 * Il garantit un contraste accessible (WCAG AA minimum 4.5:1) et des micro-interactions.
 * 
 * Variants disponibles:
 * - primary: Bouton principal vert (actions importantes)
 * - secondary: Bouton secondaire outline (actions secondaires)
 * - ghost: Bouton transparent (navigation, annulation)
 * - danger: Bouton rouge (suppression, actions destructives)
 * - ai: Bouton violet dégradé (fonctionnalités IA)
 * - premium: Bouton orange/doré (upgrade, premium)
 * - outline: Bouton bordure simple
 * 
 * Micro-interactions incluses:
 * - Hover lift effect (translateY)
 * - Press effect (scale down)
 * - Focus ring accessible
 * - Loading state avec spinner
 * - Ripple effect optionnel
 * - Success/error state feedback
 */

import React, { useState, useRef } from "react";
import {
  Button as ChakraButton,
  Icon,
  keyframes,
  Box,
  Spinner,
} from "@chakra-ui/react";
import { IoCheckmark } from "react-icons/io5";

// ============================================
// ANIMATIONS
// ============================================

const rippleAnimation = keyframes`
  0% { transform: scale(0); opacity: 0.5; }
  100% { transform: scale(4); opacity: 0; }
`;

const successPulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

// ============================================
// VARIANT STYLES
// ============================================

const variantStyles = {
  // Bouton principal - Vert Mufyn
  primary: {
    bg: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
    color: "#ffffff",
    border: "none",
    boxShadow: "0 4px 14px rgba(34, 197, 94, 0.35)",
    _hover: {
      bg: "linear-gradient(135deg, #4ade80 0%, #22c55e 100%)",
      color: "#ffffff", // Preserve white text on hover
      boxShadow: "0 8px 25px rgba(34, 197, 94, 0.45)",
      transform: "translateY(-2px)",
    },
    _active: {
      bg: "linear-gradient(135deg, #16a34a 0%, #15803d 100%)",
      color: "#ffffff",
      transform: "translateY(0) scale(0.98)",
      boxShadow: "0 2px 8px rgba(34, 197, 94, 0.3)",
    },
    _focus: {
      boxShadow: "0 0 0 3px rgba(34, 197, 94, 0.4), 0 4px 14px rgba(34, 197, 94, 0.35)",
      color: "#ffffff",
    },
  },

  // Bouton secondaire - Outline blanc/gris
  secondary: {
    bg: "transparent",
    color: "#e2e8f0", // gray.200 - bon contraste sur fond sombre
    border: "1px solid",
    borderColor: "rgba(226, 232, 240, 0.3)",
    boxShadow: "none",
    _hover: {
      bg: "rgba(255, 255, 255, 0.08)",
      color: "#f1f5f9", // slate.100 - bien visible
      borderColor: "rgba(226, 232, 240, 0.5)",
      transform: "translateY(-1px)",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
    },
    _active: {
      bg: "rgba(255, 255, 255, 0.12)",
      color: "#f1f5f9",
      transform: "translateY(0) scale(0.98)",
    },
    _focus: {
      boxShadow: "0 0 0 3px rgba(226, 232, 240, 0.3)",
      borderColor: "rgba(226, 232, 240, 0.6)",
      color: "#f1f5f9",
    },
  },

  // Bouton ghost - Transparent, pour navigation
  ghost: {
    bg: "transparent",
    color: "#94a3b8", // gray.400 - contraste suffisant
    border: "none",
    boxShadow: "none",
    _hover: {
      bg: "rgba(255, 255, 255, 0.06)",
      color: "#f1f5f9", // slate.100 - bien visible sur fond sombre
      transform: "translateY(-1px)",
    },
    _active: {
      bg: "rgba(255, 255, 255, 0.1)",
      color: "#f1f5f9",
      transform: "scale(0.98)",
    },
    _focus: {
      boxShadow: "0 0 0 2px rgba(148, 163, 184, 0.4)",
      color: "#f1f5f9",
    },
  },

  // Bouton danger - Rouge pour actions destructives
  danger: {
    bg: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
    color: "#ffffff",
    border: "none",
    boxShadow: "0 4px 14px rgba(239, 68, 68, 0.35)",
    _hover: {
      bg: "linear-gradient(135deg, #f87171 0%, #ef4444 100%)",
      color: "#ffffff", // Preserve white text on hover
      boxShadow: "0 8px 25px rgba(239, 68, 68, 0.45)",
      transform: "translateY(-2px)",
    },
    _active: {
      bg: "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
      color: "#ffffff",
      transform: "translateY(0) scale(0.98)",
    },
    _focus: {
      boxShadow: "0 0 0 3px rgba(239, 68, 68, 0.4), 0 4px 14px rgba(239, 68, 68, 0.35)",
      color: "#ffffff",
    },
  },

  // Bouton AI - Violet dégradé
  ai: {
    bg: "linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)",
    color: "#ffffff",
    border: "none",
    boxShadow: "0 4px 14px rgba(139, 92, 246, 0.35)",
    _hover: {
      bg: "linear-gradient(135deg, #a78bfa 0%, #8b5cf6 100%)",
      color: "#ffffff", // Preserve white text on hover
      boxShadow: "0 8px 25px rgba(139, 92, 246, 0.45)",
      transform: "translateY(-2px)",
    },
    _active: {
      bg: "linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)",
      color: "#ffffff",
      transform: "translateY(0) scale(0.98)",
    },
    _focus: {
      boxShadow: "0 0 0 3px rgba(139, 92, 246, 0.4), 0 4px 14px rgba(139, 92, 246, 0.35)",
      color: "#ffffff",
    },
  },

  // Bouton Premium - Orange/doré
  premium: {
    bg: "linear-gradient(135deg, #f97316 0%, #ea580c 100%)",
    color: "#ffffff",
    border: "none",
    boxShadow: "0 4px 14px rgba(249, 115, 22, 0.35)",
    _hover: {
      bg: "linear-gradient(135deg, #fb923c 0%, #f97316 100%)",
      color: "#ffffff", // Preserve white text on hover
      boxShadow: "0 8px 25px rgba(249, 115, 22, 0.45)",
      transform: "translateY(-2px)",
    },
    _active: {
      bg: "linear-gradient(135deg, #ea580c 0%, #c2410c 100%)",
      color: "#ffffff",
      transform: "translateY(0) scale(0.98)",
    },
    _focus: {
      boxShadow: "0 0 0 3px rgba(249, 115, 22, 0.4), 0 4px 14px rgba(249, 115, 22, 0.35)",
      color: "#ffffff",
    },
  },

  // Bouton outline - Bordure colorée
  outline: {
    bg: "transparent",
    color: "#4ade80", // green.400
    border: "1px solid",
    borderColor: "#4ade80",
    boxShadow: "none",
    _hover: {
      bg: "rgba(74, 222, 128, 0.1)",
      color: "#86efac", // green.300 - plus clair au hover
      borderColor: "#22c55e",
      transform: "translateY(-1px)",
      boxShadow: "0 4px 12px rgba(34, 197, 94, 0.2)",
    },
    _active: {
      bg: "rgba(74, 222, 128, 0.15)",
      color: "#4ade80",
      transform: "translateY(0) scale(0.98)",
    },
    _focus: {
      boxShadow: "0 0 0 3px rgba(74, 222, 128, 0.3)",
      color: "#86efac",
    },
  },

  // Outline bleu
  "outline-blue": {
    bg: "transparent",
    color: "#60a5fa", // blue.400
    border: "1px solid",
    borderColor: "rgba(96, 165, 250, 0.5)",
    boxShadow: "none",
    _hover: {
      bg: "rgba(96, 165, 250, 0.1)",
      color: "#93c5fd", // blue.300 - plus clair au hover
      borderColor: "#60a5fa",
      transform: "translateY(-1px)",
      boxShadow: "0 4px 12px rgba(59, 130, 246, 0.2)",
    },
    _active: {
      bg: "rgba(96, 165, 250, 0.15)",
      color: "#60a5fa",
      transform: "translateY(0) scale(0.98)",
    },
    _focus: {
      boxShadow: "0 0 0 3px rgba(96, 165, 250, 0.3)",
      color: "#93c5fd",
    },
  },

  // Outline violet
  "outline-purple": {
    bg: "transparent",
    color: "#a78bfa", // purple.400
    border: "1px solid",
    borderColor: "rgba(167, 139, 250, 0.5)",
    boxShadow: "none",
    _hover: {
      bg: "rgba(167, 139, 250, 0.1)",
      color: "#c4b5fd", // purple.300 - plus clair au hover
      borderColor: "#a78bfa",
      transform: "translateY(-1px)",
      boxShadow: "0 4px 12px rgba(139, 92, 246, 0.2)",
    },
    _active: {
      bg: "rgba(167, 139, 250, 0.15)",
      color: "#a78bfa",
      transform: "translateY(0) scale(0.98)",
    },
    _focus: {
      boxShadow: "0 0 0 3px rgba(167, 139, 250, 0.3)",
      color: "#c4b5fd",
    },
  },

  // Link style
  link: {
    bg: "transparent",
    color: "#4ade80",
    border: "none",
    boxShadow: "none",
    textDecoration: "none",
    padding: "0",
    height: "auto",
    minW: "auto",
    _hover: {
      color: "#22c55e",
      textDecoration: "underline",
      bg: "transparent",
    },
    _active: {
      color: "#16a34a",
    },
    _focus: {
      boxShadow: "none",
    },
  },
};

// ============================================
// SIZE STYLES
// ============================================

const sizeStyles = {
  xs: {
    h: "28px",
    px: "12px",
    fontSize: "xs",
    borderRadius: "8px",
  },
  sm: {
    h: "36px",
    px: "16px",
    fontSize: "sm",
    borderRadius: "10px",
  },
  md: {
    h: "44px",
    px: "20px",
    fontSize: "sm",
    borderRadius: "12px",
  },
  lg: {
    h: "52px",
    px: "28px",
    fontSize: "md",
    borderRadius: "14px",
  },
  xl: {
    h: "60px",
    px: "36px",
    fontSize: "lg",
    borderRadius: "16px",
  },
};

// ============================================
// COMPONENT
// ============================================

const MufynButton = React.forwardRef(({
  variant = "primary",
  size = "md",
  children,
  leftIcon,
  rightIcon,
  isLoading = false,
  loadingText,
  isDisabled = false,
  isSuccess = false,
  withRipple = false,
  withShimmer = false,
  fullWidth = false,
  ...props
}, ref) => {
  const [ripples, setRipples] = useState([]);
  const buttonRef = useRef(null);

  // Ripple effect handler
  const handleRipple = (e) => {
    if (!withRipple || isDisabled || isLoading) return;
    
    const button = buttonRef.current;
    if (!button) return;
    
    const rect = button.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    const newRipple = {
      x,
      y,
      id: Date.now(),
    };
    
    setRipples((prev) => [...prev, newRipple]);
    
    setTimeout(() => {
      setRipples((prev) => prev.filter((r) => r.id !== newRipple.id));
    }, 600);
  };

  const styles = variantStyles[variant] || variantStyles.primary;
  const sizes = sizeStyles[size] || sizeStyles.md;

  // Disabled styles
  const disabledStyles = {
    opacity: 0.5,
    cursor: "not-allowed",
    transform: "none !important",
    boxShadow: "none !important",
  };

  // Success state styles
  const successStyles = isSuccess ? {
    bg: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
    color: "#ffffff",
    animation: `${successPulse} 0.3s ease`,
  } : {};

  // Filter out style props that could override our variant styles
  const {
    bg, background, color, _hover, _active, _focus, colorScheme,
    ...safeProps
  } = props;

  return (
    <ChakraButton
      ref={(node) => {
        buttonRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) ref.current = node;
      }}
      position="relative"
      overflow="hidden"
      fontWeight="600"
      letterSpacing="0.01em"
      transition="all 0.2s cubic-bezier(0.4, 0, 0.2, 1)"
      w={fullWidth ? "100%" : "auto"}
      {...safeProps}
      {...sizes}
      {...styles}
      {...(isDisabled && disabledStyles)}
      {...successStyles}
      isDisabled={isDisabled || isLoading}
      onClick={(e) => {
        handleRipple(e);
        safeProps.onClick?.(e);
      }}
      aria-busy={isLoading}
      _disabled={disabledStyles}
    >
      {/* Shimmer effect overlay */}
      {withShimmer && !isLoading && !isDisabled && (
        <Box
          position="absolute"
          top="0"
          left="0"
          right="0"
          bottom="0"
          bg="linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent)"
          backgroundSize="200% 100%"
          animation={`${shimmer} 2s linear infinite`}
          pointerEvents="none"
          borderRadius="inherit"
        />
      )}

      {/* Ripple effects */}
      {withRipple && ripples.map((ripple) => (
        <Box
          key={ripple.id}
          position="absolute"
          left={`${ripple.x}px`}
          top={`${ripple.y}px`}
          w="10px"
          h="10px"
          bg="rgba(255, 255, 255, 0.3)"
          borderRadius="full"
          transform="translate(-50%, -50%)"
          animation={`${rippleAnimation} 0.6s ease-out`}
          pointerEvents="none"
        />
      ))}

      {/* Content */}
      {isLoading ? (
        <>
          <Spinner size="sm" mr={loadingText ? 2 : 0} />
          {loadingText}
        </>
      ) : isSuccess ? (
        <>
          <Icon as={IoCheckmark} mr={children ? 2 : 0} />
          {children}
        </>
      ) : (
        <>
          {leftIcon && <Box as="span" mr="8px" display="inline-flex" alignItems="center">{leftIcon}</Box>}
          {children}
          {rightIcon && <Box as="span" ml="8px" display="inline-flex" alignItems="center">{rightIcon}</Box>}
        </>
      )}
    </ChakraButton>
  );
});

MufynButton.displayName = "MufynButton";

export default MufynButton;
